
import React, { useState } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { PhoneCall } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';

// Define form types
interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string; // Added phone field to form values
  company: string;
  annualRevenue: string;
  businessPhase: string;
  teamSize: string;
  role: string;
  challenges: string[];
  urgency: string;
  supportType: string;
  additionalInfo: string;
}

const Contact = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const phoneNumber = "+1 (860)-982-5635";
  const phoneNumberFormatted = phoneNumber.replace(/[^\d+]/g, "");
  const email = "Kristen.Tangarone@gmail.com";
  
  // Form step state
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Setup form with react-hook-form
  const form = useForm<FormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '', // Added default value for phone
      company: '',
      annualRevenue: '',
      businessPhase: '',
      teamSize: '',
      role: '',
      challenges: [],
      urgency: '',
      supportType: '',
      additionalInfo: '',
    }
  });
  
  // Handle navigation between form steps
  const nextStep = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent form submission on navigation
    e.preventDefault();
    
    if (step === 1) {
      const isValid = await form.trigger(['firstName', 'lastName', 'email']);
      if (isValid) setStep(step + 1);
    } else if (step === 2) {
      // Move to the third step without submitting the form
      setStep(step + 1);
    }
  };
  
  const prevStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setStep(step - 1);
  };
  
  // Handle form submission
  const onSubmit: SubmitHandler<FormValues> = (data) => {
    // Only process submission on the final step
    if (step !== totalSteps) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Format challenges array into readable text
    const challengesText = data.challenges.length > 0 ? 
      `Challenges: ${data.challenges.join(', ')}` : '';
    
    // Create subject line
    const fullName = `${data.firstName} ${data.lastName}`.trim();
    const subject = `Consultation Request from ${fullName}${data.company ? ` (${data.company})` : ''}`;
    
    // Build detailed email body with all qualification information
    const emailBody = `
Hello Kristen,
    
I'm interested in scheduling a consultation for your services. Here's some information about my business:
    
Contact Information:
- Name: ${fullName}
- Email: ${data.email}
- Phone: ${data.phone || 'Not provided'} 
- Company: ${data.company || 'N/A'}
- Role: ${data.role || 'N/A'}
    
Business Profile:
- Annual Revenue: ${data.annualRevenue || 'Not specified'}
- Business Phase: ${data.businessPhase || 'Not specified'}
- Team Size: ${data.teamSize || 'Not specified'}
    
Business Needs:
${challengesText}
- Urgency: ${data.urgency || 'Not specified'}
- Support Type Interested In: ${data.supportType || 'Not specified'}
    
Additional Information:
${data.additionalInfo || 'No additional information provided.'}
    
Looking forward to your response.
    
Best regards,
${fullName}
${data.company ? `\n${data.company}` : ''}
`;
    
    // Create mailto link
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Show success toast
    toast({
      title: "Email client opened",
      description: "Your consultation request has been prepared for sending.",
    });
    
    setIsSubmitting(false);
  };
  
  // Definition of challenge options
  const challengeOptions = [
    { id: "inefficient-processes", label: "Inefficient or outdated processes" },
    { id: "leadership-issues", label: "Lack of leadership accountability or training" },
    { id: "scaling-difficulty", label: "Difficulty scaling operations" },
    { id: "day-to-day-stuck", label: "Feeling stuck in the day-to-day" },
    { id: "no-direction", label: "No clear strategic direction" },
    { id: "other", label: "Other" }
  ];

  return (
    <section id="contact" className="py-12 md:py-16 lg:py-24 bg-cream">
      <div className="container px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
          <div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-maroon mb-5 md:mb-6">
              Let's Work Together
            </h2>
            <p className="text-base md:text-lg text-charcoal-light mb-6 md:mb-8">
              Ready to transform your operations and scale with confidence? Schedule your free strategy session today.
            </p>
            <div className="space-y-5 md:space-y-6">
              <div className="flex items-start">
                <div className="mr-4 mt-1 bg-maroon/10 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#722F37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
                <div className="flex items-center gap-2">
                  <div>
                    <h3 className="font-heading font-medium text-lg text-charcoal">Phone</h3>
                    <p className="text-charcoal-light">{phoneNumber}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-2 bg-maroon/10 hover:bg-maroon/20 border-maroon/20 text-maroon"
                    asChild
                  >
                    <a href={`tel:${phoneNumberFormatted}`}>
                      <PhoneCall className="mr-1" size={16} />
                      Call
                    </a>
                  </Button>
                </div>
              </div>

              <div className="flex items-start">
                <div className="mr-4 mt-1 bg-maroon/10 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#722F37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-heading font-medium text-lg text-charcoal">Email</h3>
                  <a href={`mailto:${email}`} className="text-charcoal-light hover:text-maroon">
                    {email}
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <div className="mr-4 mt-1 bg-maroon/10 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#722F37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-heading font-medium text-lg text-charcoal">Location</h3>
                  <p className="text-charcoal-light">Connecticut (Available for remote consulting)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <h3 className="font-heading font-semibold text-xl md:text-2xl text-charcoal mb-5 md:mb-6">
              Schedule Your Free Strategy Session
            </h3>
            
            {/* Progress indicator */}
            <div className="flex justify-between items-center mb-6">
              {[1, 2, 3].map((stepNumber) => (
                <div 
                  key={stepNumber}
                  className={`flex-1 h-2 rounded-full mx-1 ${
                    stepNumber <= step ? 'bg-maroon' : 'bg-maroon/20'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-charcoal-light mb-4">
              Step {step} of {totalSteps}: 
              {step === 1 ? " Your Information" : 
               step === 2 ? " Business Details" : 
               " Business Needs"}
            </p>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 md:space-y-5">
                {/* Step 1: Basic contact information */}
                {step === 1 && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                      <FormField
                        control={form.control}
                        name="firstName"
                        rules={{ required: "First name is required" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="block text-sm font-medium text-charcoal mb-1">
                              First Name
                            </FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="First Name"
                                className="border-maroon/20 focus:border-maroon focus:ring-maroon h-12"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="lastName"
                        rules={{ required: "Last name is required" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="block text-sm font-medium text-charcoal mb-1">
                              Last Name
                            </FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Last Name"
                                className="border-maroon/20 focus:border-maroon focus:ring-maroon h-12"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="email"
                      rules={{ 
                        required: "Email is required", 
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address"
                        } 
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="block text-sm font-medium text-charcoal mb-1">
                            Email
                          </FormLabel>
                          <FormControl>
                            <Input 
                              type="email" 
                              placeholder="your@email.com"
                              className="border-maroon/20 focus:border-maroon focus:ring-maroon h-12"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {/* New Phone Number field */}
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="block text-sm font-medium text-charcoal mb-1">
                            Phone Number
                          </FormLabel>
                          <FormControl>
                            <Input 
                              type="tel"
                              placeholder="(123) 456-7890"
                              className="border-maroon/20 focus:border-maroon focus:ring-maroon h-12"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="block text-sm font-medium text-charcoal mb-1">
                            Company
                          </FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Your Company"
                              className="border-maroon/20 focus:border-maroon focus:ring-maroon h-12"
                              {...field} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </>
                )}

                {/* Step 2: Business profile questions */}
                {step === 2 && (
                  <>
                    <FormField
                      control={form.control}
                      name="annualRevenue"
                      render={({ field }) => (
                        <FormItem className="space-y-3">
                          <FormLabel className="font-medium text-charcoal">
                            What is your current annual revenue?
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="space-y-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Under $500K" id="revenue-1" />
                                <Label htmlFor="revenue-1">Under $500K</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="$500K–$1M" id="revenue-2" />
                                <Label htmlFor="revenue-2">$500K–$1M</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="$1M–$5M" id="revenue-3" />
                                <Label htmlFor="revenue-3">$1M–$5M</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="$5M+" id="revenue-4" />
                                <Label htmlFor="revenue-4">$5M+</Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="businessPhase"
                      render={({ field }) => (
                        <FormItem className="space-y-3">
                          <FormLabel className="font-medium text-charcoal">
                            How would you describe the current phase of your business?
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="space-y-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Startup" id="phase-1" />
                                <Label htmlFor="phase-1">Startup</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Growth stage" id="phase-2" />
                                <Label htmlFor="phase-2">Growth stage</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Established and scaling" id="phase-3" />
                                <Label htmlFor="phase-3">Established and scaling</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Stagnant or in transition" id="phase-4" />
                                <Label htmlFor="phase-4">Stagnant or in transition</Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="teamSize"
                      render={({ field }) => (
                        <FormItem className="space-y-3">
                          <FormLabel className="font-medium text-charcoal">
                            How large is your team?
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="space-y-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Fewer than 5 employees" id="team-1" />
                                <Label htmlFor="team-1">Fewer than 5 employees</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="5–20 employees" id="team-2" />
                                <Label htmlFor="team-2">5–20 employees</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="20–50 employees" id="team-3" />
                                <Label htmlFor="team-3">20–50 employees</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="50+ employees" id="team-4" />
                                <Label htmlFor="team-4">50+ employees</Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="block font-medium text-charcoal mb-1">
                            What is your role or title within the company?
                          </FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Your role or title"
                              className="border-maroon/20 focus:border-maroon focus:ring-maroon h-12"
                              {...field} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </>
                )}
                
                {/* Step 3: Business needs questions */}
                {step === 3 && (
                  <>
                    <div className="space-y-4">
                      <FormLabel className="block font-medium text-charcoal">
                        What challenges are you currently experiencing?
                      </FormLabel>
                      <div className="space-y-2">
                        {challengeOptions.map((option) => (
                          <div key={option.id} className="flex items-start space-x-2">
                            <Checkbox 
                              id={option.id}
                              checked={form.watch('challenges')?.includes(option.label)}
                              onCheckedChange={(checked) => {
                                const currentValues = form.getValues('challenges') || [];
                                if (checked) {
                                  form.setValue('challenges', [
                                    ...currentValues,
                                    option.label
                                  ]);
                                } else {
                                  form.setValue(
                                    'challenges',
                                    currentValues.filter(value => value !== option.label)
                                  );
                                }
                              }}
                            />
                            <Label 
                              htmlFor={option.id}
                              className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="urgency"
                      render={({ field }) => (
                        <FormItem className="space-y-3">
                          <FormLabel className="font-medium text-charcoal">
                            How urgent is it for you to make changes in your operations?
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="space-y-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Just exploring options" id="urgency-1" />
                                <Label htmlFor="urgency-1">Just exploring options</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Would like help this quarter" id="urgency-2" />
                                <Label htmlFor="urgency-2">Would like help this quarter</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Actively looking for support now" id="urgency-3" />
                                <Label htmlFor="urgency-3">Actively looking for support now</Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="supportType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="block font-medium text-charcoal mb-1">
                            What kind of support are you most interested in?
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="border-maroon/20 focus:border-maroon focus:ring-maroon h-12">
                                <SelectValue placeholder="Select support type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Leadership development">Leadership development</SelectItem>
                              <SelectItem value="Process improvement">Process improvement</SelectItem>
                              <SelectItem value="Strategic planning">Strategic planning</SelectItem>
                              <SelectItem value="Team structure and role clarity">Team structure and role clarity</SelectItem>
                              <SelectItem value="Ongoing operational support (fractional COO)">Ongoing operational support (fractional COO)</SelectItem>
                              <SelectItem value="Not sure yet">Not sure yet — need help figuring that out</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="additionalInfo"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="block font-medium text-charcoal mb-1">
                            Anything else you'd like me to know about your business right now?
                          </FormLabel>
                          <FormControl>
                            <Textarea 
                              className="border-maroon/20 focus:border-maroon focus:ring-maroon min-h-[120px]"
                              placeholder="Share any additional information..."
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </>
                )}
                
                {/* Navigation buttons */}
                <div className="flex justify-between pt-2">
                  {step > 1 && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={prevStep}
                      className="border-maroon/20 text-maroon hover:bg-maroon/10"
                    >
                      Previous
                    </Button>
                  )}
                  
                  <div className={step > 1 ? 'ml-auto' : 'w-full'}>
                    {step < totalSteps ? (
                      <Button 
                        type="button"
                        className="w-full bg-maroon hover:bg-maroon-light text-white py-5 md:py-6 h-auto text-base md:text-lg"
                        onClick={nextStep}
                      >
                        Continue
                      </Button>
                    ) : (
                      <Button 
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-maroon hover:bg-maroon-light text-white py-5 md:py-6 h-auto text-base md:text-lg"
                      >
                        {isSubmitting ? "Preparing..." : "Schedule Consultation"}
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
